import React, { Component, ReactNode, useMemo } from 'react';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import type { IGatsbyImageData } from 'gatsby-plugin-image';
import type { PreviewTemplateComponentProps } from 'decap-cms-core';

interface StaticImageNode {
  relativePath: string;
  childImageSharp?: {
    gatsbyImageData: IGatsbyImageData;
  } | null;
}

interface ImageDataQuery {
  imageFiles: {
    nodes: StaticImageNode[];
  };
}

export interface MdxImageProps {
  src?: string;
  alt?: string;
  className?: string;
  imgClassName?: string;
  loading?: 'lazy' | 'eager';
  objectFit?: React.CSSProperties['objectFit'];
  width?: number;
  height?: number;
  // プレビュー環境用のprops
  getAsset?: PreviewTemplateComponentProps['getAsset'];
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * MdxImage用の内部エラーバウンダリー
 * GatsbyImageのレンダリングエラーをキャッチし、フォールバックを提供
 */
class MdxImageErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('MdxImage rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/**
 * 画像パスを正規化する
 * - 外部URLはnullを返す
 * - 先頭のスラッシュや"img/"プレフィックスを除去
 * - Unicode正規化（NFC形式）を適用
 *   macOSはNFD形式、Windows/LinuxはNFC形式を使用するため、
 *   GraphQLのrelativePathとの比較時に不一致が発生する問題を解決
 */
const normalizeSrc = (src?: string): string | null => {
  if (!src) {
    return null;
  }

  const trimmed = src.trim();
  if (!trimmed || /^https?:\/\//.test(trimmed)) {
    return null;
  }

  const pathname = trimmed.replace(/^https?:\/\/[^/]+/i, '');
  let normalized = pathname.replace(/^\/+/, '');

  if (normalized.startsWith('img/')) {
    normalized = normalized.slice(4); // Now normalized contains: 'dictionary/filename.png' or 'manga/filename.png'
  }

  // Unicode正規化（NFC形式）を適用
  // NFD形式（濁点等が分離）をNFC形式（合成済み）に統一
  return normalized.normalize('NFC');
};

const MdxImageInner: React.FC<MdxImageProps> = ({
  src,
  alt,
  className,
  imgClassName,
  loading = 'lazy',
  objectFit = 'cover',
  width,
  height,
  getAsset,
}) => {
  let data: ImageDataQuery | null = null;

  try {
    data = useStaticQuery<ImageDataQuery>(graphql`
      query MdxImageData {
        imageFiles: allFile(
          filter: {
            sourceInstanceName: { eq: "uploaded-images" }
            extension: { in: ["png", "jpg", "jpeg", "webp", "gif"] }
          }
        ) {
          nodes {
            relativePath
            childImageSharp {
              gatsbyImageData(
                placeholder: BLURRED
                formats: [AUTO, WEBP]
                layout: CONSTRAINED
                width: 960
              )
            }
          }
        }
      }
    `);
  } catch (error) {
    console.error('MdxImage StaticQuery error:', error);
    // StaticQueryが利用できない環境（CMSプレビュー等）ではnullを設定
    data = null;
  }

  const imageNode = useMemo(() => {
    const normalized = normalizeSrc(src);
    if (!normalized || !data?.imageFiles?.nodes) {
      return null;
    }

    // GraphQLから取得したrelativePathもNFC形式に正規化して比較
    // ファイルシステムによってNFD/NFC形式が異なるため
    return (
      data.imageFiles.nodes.find(
        node => node.relativePath.normalize('NFC') === normalized
      ) ?? null
    );
  }, [data?.imageFiles?.nodes, src]);

  const imageData = imageNode?.childImageSharp?.gatsbyImageData as
    | IGatsbyImageData
    | undefined;
  const altText = alt || '画像';
  const imgStyle: React.CSSProperties = {
    objectFit,
    ...(width && { width }),
    ...(height && { height }),
  };

  // プレビュー環境（getAssetが提供されている場合）の処理
  if (getAsset && src) {
    const asset = getAsset(src);
    const imageUrl = asset?.url || withPrefix(src);

    imgStyle.marginLeft = 'auto';
    imgStyle.marginRight = 'auto';

    return (
      <img
        src={imageUrl}
        alt={altText}
        className={className}
        loading={loading}
        style={imgStyle}
        width={width}
        height={height}
      />
    );
  }

  // フォールバック用のimg要素
  const imgFallback = src ? (
    <img
      src={withPrefix(src)}
      alt={altText}
      className={className}
      loading={loading}
      style={imgStyle}
      width={width}
      height={height}
    />
  ) : null;

  // StaticQueryが利用できない環境（CMSプレビュー等）では常にフォールバックを使用
  if (!data) {
    return imgFallback;
  }

  if (imageData) {
    // GatsbyImageをエラーバウンダリーで包む
    return (
      <MdxImageErrorBoundary fallback={imgFallback}>
        <GatsbyImage
          image={imageData as IGatsbyImageData}
          alt={altText}
          className={className}
          imgClassName={imgClassName}
          loading={loading}
          imgStyle={imgStyle}
        />
      </MdxImageErrorBoundary>
    );
  }

  return imgFallback;
};

const MdxImage: React.FC<MdxImageProps> = props => {
  return (
    <MdxImageErrorBoundary
      fallback={
        props.src ? (
          <img
            src={withPrefix(props.src)}
            alt={props.alt || '画像'}
            className={props.className}
            loading={props.loading || 'lazy'}
            style={{
              objectFit: props.objectFit || 'cover',
              ...(props.width && { width: props.width }),
              ...(props.height && { height: props.height }),
            }}
            width={props.width}
            height={props.height}
          />
        ) : null
      }
    >
      <MdxImageInner {...props} />
    </MdxImageErrorBoundary>
  );
};

export default MdxImage;
